import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  NavBarContainer,
  LogoContainer,
  LogoImage,
  Hamburger,
  NavList,
  NavItem,
  NavLink,
  StallNavLink,
  DropdownContainer,
  DropdownList,
  DropdownItem,
  DropdownLink,
  VisitorCounterContainer,
  VisitorCount,
  VisitorCountLabel,
  MobileMenuHeader,
} from './styles/NavBar.styles';
import logo from '../../images/gremaglobal.png';
import { doc, getDoc, increment, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface NavBarProps {
  isLoggedIn: boolean;
  onLogin: () => void;
  onSignup: () => void;
  onLogout: () => void;
  onHomeClick: () => void;
}

const HamburgerIcon: React.FC<{ isOpen: boolean; onClick: () => void }> = ({ isOpen, onClick }) => (
  <Hamburger onClick={onClick} isOpen={isOpen}>
    <div />
    <div />
    <div />
  </Hamburger>
);

const NavBar: React.FC<NavBarProps> = ({ isLoggedIn, onHomeClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isStallActionsOpen, setIsStallActionsOpen] = useState(false);
  const [visitorCount, setVisitorCount] = useState<string>('Loading...');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const counterRef = doc(db, "counters", "visitors");
    const STORAGE_KEY = "grema_last_visit";
    // const VISIT_COOLDOWN = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const VISIT_COOLDOWN = 60 * 1000; // 1 minute in milliseconds

    const updateVisitorCount = async () => {
      try {
        const lastVisit = localStorage.getItem(STORAGE_KEY);
        const now = Date.now();

        // Check if user has visited in the last 24 hours
        if (lastVisit && now - parseInt(lastVisit) < VISIT_COOLDOWN) {
          // Already counted recently -> Just read the current count
          const snap = await getDoc(counterRef);
          if (snap.exists()) {
            setVisitorCount(snap.data().count?.toLocaleString() ?? '0');
          }
          return;
        }

        // It's a new unique visit or 24h passed -> Increment
        localStorage.setItem(STORAGE_KEY, now.toString());

        const docSnap = await getDoc(counterRef);

        if (!docSnap.exists()) {
          // Initialize if it doesn't exist
          await setDoc(counterRef, { count: 1 });
        } else {
          // Atomic increment safely handles multiple simultaneous visitors
          await updateDoc(counterRef, { count: increment(1) });
        }

        // Fetch the fresh value
        const freshSnap = await getDoc(counterRef);
        if (freshSnap.exists()) {
          setVisitorCount(freshSnap.data().count?.toLocaleString() ?? '0');
        }
      } catch (err: any) {
        console.error("Visitor counter error:", err.message);
        setVisitorCount("—"); // Fallback for UI
      }
    };

    updateVisitorCount();

    // Refresh every 30 seconds to show latest activity without incrementing
    const interval = setInterval(async () => {
      try {
        const snap = await getDoc(counterRef);
        if (snap.exists()) {
          setVisitorCount(snap.data().count?.toLocaleString() ?? '0');
        }
      } catch (e) { /* silent fail */ }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isDropdownOpen) setIsDropdownOpen(false);
    if (isStallActionsOpen) setIsStallActionsOpen(false);
  };

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleStallActionsDropdown = () => setIsStallActionsOpen(!isStallActionsOpen);

  const handleNavLinkClick = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    setIsStallActionsOpen(false);
  };

  return (
    <>
      {/* Main sticky navbar - always visible */}
      <NavBarContainer>
        <LogoContainer onClick={onHomeClick}>
          <LogoImage src={logo} alt="Grema Logo" />
        Affordable, Genuine, Global       
        </LogoContainer>

        {/* Single NavList used for both desktop and mobile */}
        <NavList isOpen={isMenuOpen}>
          <NavItem>
            <NavLink 
              $isActive={location.pathname === '/'} 
              $colorType="success" 
              onClick={() => handleNavLinkClick('/')}
            >
              Home
            </NavLink>
          </NavItem>

          <NavItem>
            <StallNavLink 
              $isActive={location.pathname === '/buy-stall'} 
              $type="buy"
              onClick={() => handleNavLinkClick('/buy-stall')}
            >
              Advertise Here
            </StallNavLink>
          </NavItem>

          {isLoggedIn && (
            <>
              <NavItem>
                <StallNavLink 
                  $isActive={location.pathname === '/stall-login'} 
                  $type="update"
                  onClick={() => handleNavLinkClick('/stall-login')}
                >
                  Update Stall
                </StallNavLink>
              </NavItem>
              <NavItem>
                <StallNavLink 
                  $isActive={location.pathname === '/renewal-login'} 
                  $type="renew"
                  onClick={() => handleNavLinkClick('/renewal-login')}
                >
                  Renew Stall
                </StallNavLink>
              </NavItem>
            </>
          )}

          <NavItem>
            <NavLink 
              $isActive={location.pathname === '/policy'} 
              $colorType="success" 
              onClick={() => handleNavLinkClick('/policy')}
            >
              Our Policy
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink 
              $isActive={location.pathname === '/services'} 
              $colorType="success" 
              onClick={() => handleNavLinkClick('/services')}
            >
              Our Services
            </NavLink>
          </NavItem>

          <NavItem>
            <DropdownContainer 
            >
              <NavLink 
                as="div" 
                onClick={toggleDropdown} 
                $colorType="success"
              >
                About Us
              </NavLink>
              {isDropdownOpen && (
                <DropdownList>
                  <DropdownItem>
                    <DropdownLink onClick={() => handleNavLinkClick('/why-grema')}>
                      Why GREMA
                    </DropdownLink>
                  </DropdownItem>
                  
                  <DropdownItem>
                    <DropdownLink onClick={() => handleNavLinkClick('/usermanual')}>
                      User Guide
                    </DropdownLink>
                  </DropdownItem>
                  <DropdownItem>
                    <DropdownLink onClick={() => handleNavLinkClick('/contact')}>
                      Contact Us
                    </DropdownLink>
                  </DropdownItem>
                  <DropdownItem>
                    <DropdownLink onClick={() => handleNavLinkClick('/partners')}>
                      Partners
                    </DropdownLink>
                  </DropdownItem>
                  <DropdownItem>
                    <DropdownLink onClick={() => handleNavLinkClick('/promotions')}>
                      Promotions
                    </DropdownLink>
                  </DropdownItem>
                </DropdownList>
              )}
            </DropdownContainer>
          </NavItem>

          {/* Visitor counter - visible only on desktop */}
          {isDesktop && (
            <NavItem className="desktop-visitor">
              <VisitorCounterContainer>
                <VisitorCountLabel>Visitors:</VisitorCountLabel>
                <VisitorCount>{visitorCount}</VisitorCount>
              </VisitorCounterContainer>
            </NavItem>
          )}
        </NavList>

        <HamburgerIcon isOpen={isMenuOpen} onClick={toggleMenu} />
      </NavBarContainer>

      {/* Mobile-only top bar with visitor counter in the middle */}
      <MobileMenuHeader>
        <LogoContainer onClick={onHomeClick}>
          <LogoImage src={logo} alt="Grema Logo" />
           Affordable, Genuine, Global
        </LogoContainer>

        <VisitorCounterContainer>
          <VisitorCountLabel>Visitors:</VisitorCountLabel>
          <VisitorCount>{visitorCount}</VisitorCount>
        </VisitorCounterContainer>

        <HamburgerIcon isOpen={isMenuOpen} onClick={toggleMenu} />
      </MobileMenuHeader>
    </>
  );
};

export default NavBar;